import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Checkbox, FormControlLabel, Grid, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { Navigate } from 'react-router-dom';
import apiClient from '../../ApiClient/ApiClient';
import DemandImportDialog from './DemandImportDialog';
import { blankDemand } from './demandImageParser';

const today = () => dayjs().format('YYYY-MM-DD');
const newForm = () => ({
  ...blankDemand(), sourceKind: 'Manual',
  requestId: uuidv4(), workDate: today(), drawingNo: '', itemName: '', specification: '',
  unit: 'PCS', goodQuantity: '', operatorName: '', workstation: '', shift: '', remark: '', defects: [],
});
const errorText = (error) => error.response?.data?.message || '連線失敗，請稍後重試。';
const unwrap = (response) => {
  if (!response.data?.success) throw new Error(response.data?.message || '作業失敗');
  return response.data.data;
};
const timestamp = (value) => value ? dayjs(`${value}${/[zZ]|[+-]\d{2}:\d{2}$/.test(value) ? '' : 'Z'}`).format('YYYY/MM/DD HH:mm:ss') : '';
const actionNames = { Create: '新增', Update: '修改', Void: '作廢' };

export default function ProductionReport() {
  const signedIn = Boolean(sessionStorage.getItem('token') && sessionStorage.getItem('Id') && sessionStorage.getItem('company'));
  const [permissions, setPermissions] = useState(null);
  const [filters, setFilters] = useState({ from: dayjs().startOf('month').format('YYYY-MM-DD'), to: today(), keyword: '', status: 'Active' });
  const [query, setQuery] = useState(null);
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [result, setResult] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(newForm);
  const [dirty, setDirty] = useState(false);
  const [formError, setFormError] = useState('');
  const [history, setHistory] = useState([]);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const lock = useRef(false);

  useEffect(() => {
    if (!signedIn) return undefined;
    let active = true;
    apiClient.get('/ProductionReport/Permissions').then(unwrap).then((data) => {
      if (active) setPermissions(data);
    }).catch((error) => { if (active) setMessage({ severity: 'error', text: errorText(error) }); });
    return () => { active = false; };
  }, [signedIn]);

  useEffect(() => {
    if (!permissions?.view || !query) return undefined;
    const controller = new AbortController();
    setLoading(true); setMessage(null);
    apiClient.get('/ProductionReport', { params: { ...query, page }, signal: controller.signal })
      .then(unwrap).then(setResult).catch((error) => {
        if (!controller.signal.aborted) { setResult({ items: [], total: 0 }); setMessage({ severity: 'error', text: errorText(error) }); }
      }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [permissions, query, page, reload]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const search = () => {
    if (!filters.from || !filters.to || filters.to < filters.from || dayjs(filters.to).diff(dayjs(filters.from), 'day') > 366) {
      setMessage({ severity: 'error', text: '請指定有效起訖日期，每次最多查詢 366 天。' }); return;
    }
    setPage(1); setQuery({ ...filters }); setReload((n) => n + 1);
  };
  const change = (key, value) => {
    setForm((previous) => ({ ...previous, [key]: value,
      ...(previous.sourceKind !== 'Manual' && ['sourceDocumentNo', 'sourceLineNo', 'customerName', 'model', 'drawingNo', 'itemName', 'demandQuantity', 'plannedDeliveryDate', 'deliveryDateText', 'productionBatchNo', 'demandRemark'].includes(key) ? { sourceConfirmed: false } : {}) }));
    setDirty(true);
  };
  const close = () => {
    if (busy || (dirty && !window.confirm('尚未儲存，確定放棄本次輸入？'))) return;
    setDialog(null); setDirty(false); setFormError('');
  };
  const create = () => { setForm(newForm()); setHistory([]); setFormError(''); setDirty(false); setDialog('create'); };
  const open = async (row) => {
    if (lock.current) return;
    lock.current = true; setBusy(true);
    try {
      const data = unwrap(await apiClient.get(`/ProductionReport/${row.id}`));
      setForm({ ...newForm(), ...data, workDate: data.workDate.slice(0, 10), plannedDeliveryDate: data.plannedDeliveryDate?.slice(0, 10) || '' }); setHistory([]);
      setFormError(''); setDirty(false); setDialog('view');
    } catch (error) { setMessage({ severity: 'error', text: errorText(error) }); }
    finally { lock.current = false; setBusy(false); }
  };
  const save = async () => {
    if (lock.current) return;
    setFormError('');
    const good = Number(form.goodQuantity);
    const defects = form.defects.map((x) => ({ reason: x.reason.trim(), quantity: Number(x.quantity) }));
    const validQuantity = (value) => Number.isFinite(value) && value >= 0 && value <= 9999999999 && Math.abs(value * 10000 - Math.round(value * 10000)) < 0.01;
    if (!form.workDate || !form.drawingNo.trim() || !form.unit.trim() || !form.operatorName.trim() || form.goodQuantity === '' ||
      !validQuantity(good) || defects.some((x) => !x.reason || !validQuantity(x.quantity) || x.quantity <= 0) ||
      good + defects.reduce((sum, x) => sum + x.quantity, 0) <= 0) {
      setFormError('請填寫日期、圖號、單位、生產人員與良品數量；不良需填原因及正數數量，數量最多四位小數，總數須大於 0。'); return;
    }
    lock.current = true; setBusy(true);
    try {
      if (form.demandQuantity !== '' && form.demandQuantity !== null && !validQuantity(Number(form.demandQuantity))) {
        setFormError('需求數量必須為非負數，最多四位小數且不得超過上限。'); return;
      }
      const payload = { ...form, goodQuantity: good, defects, demandQuantity: form.demandQuantity === '' || form.demandQuantity === null ? null : Number(form.demandQuantity), plannedDeliveryDate: form.plannedDeliveryDate || null };
      const response = dialog === 'create' ? await apiClient.post('/ProductionReport', payload)
        : await apiClient.put(`/ProductionReport/${form.id}`, payload);
      const saved = unwrap(response);
      setForm({ ...newForm(), ...saved, workDate: saved.workDate.slice(0, 10), plannedDeliveryDate: saved.plannedDeliveryDate?.slice(0, 10) || '' }); setDialog('view'); setDirty(false); setHistory([]);
      setMessage({ severity: 'success', text: `已儲存 ${saved.reportNo}。測試紀錄不影響庫存。` });
      if (query) setReload((n) => n + 1);
    } catch (error) { setFormError(errorText(error)); }
    finally { lock.current = false; setBusy(false); }
  };
  const remove = async () => {
    if (lock.current || !voidReason.trim()) return;
    lock.current = true; setBusy(true); setFormError('');
    try {
      const saved = unwrap(await apiClient.delete(`/ProductionReport/${form.id}`, { data: { version: form.version, reason: voidReason.trim() } }));
      setForm({ ...newForm(), ...saved, workDate: saved.workDate.slice(0, 10), plannedDeliveryDate: saved.plannedDeliveryDate?.slice(0, 10) || '' }); setVoidOpen(false); setHistory([]);
      setMessage({ severity: 'success', text: '已作廢，原始紀錄與異動歷程均保留。' });
      if (query) setReload((n) => n + 1);
    } catch (error) { setFormError(errorText(error)); }
    finally { lock.current = false; setBusy(false); }
  };
  const loadHistory = async () => {
    setBusy(true);
    try { setHistory(unwrap(await apiClient.get(`/ProductionReport/${form.id}/History`))); }
    catch (error) { setFormError(errorText(error)); }
    finally { setBusy(false); }
  };
  const editable = dialog === 'create' || dialog === 'edit';
  const defectTotal = form.defects.reduce((sum, x) => sum + (Number(x.quantity) || 0), 0);
  const field = (label, key, maxLength, required = false, type = 'text') => (
    <Grid item xs={12} sm={6} md={4} key={key}>
      <TextField fullWidth size="small" label={label} value={form[key] ?? ''} required={required} type={type}
        disabled={!editable || busy} onChange={(event) => change(key, event.target.value)}
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
        inputProps={type === 'number' ? { min: 0, step: '0.0001' } : { maxLength }} />
    </Grid>
  );

  if (!signedIn) return <Navigate to="/login" replace />;
  return <Box sx={{ p: { xs: 1, md: 3 } }}>
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
      <Typography variant="h5">成品報工紀錄</Typography><Chip label="測試版" color="warning" />
    </Stack>
    <Alert severity="info" sx={{ mb: 2 }}>僅保存成品報工與不良紀錄，不扣料、不入庫、不連動製令或 MES。可從 Excel／照片帶入需求資料，實際良品與不良須另填；單位仍須人工確認，尚未核對料品主檔。</Alert>
    {permissions?.view && permissions?.create && <Button variant="outlined" sx={{ mb: 2 }} disabled={busy} onClick={() => setImportOpen(true)}>從需求單帶入（Excel／拍照）</Button>}
    {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}
    {!permissions && !message && <Typography>載入權限中…</Typography>}
    {permissions && !permissions.view && <Alert severity="warning">尚無報工查詢權限，請管理者至使用者權限的「生產／資材卡」設定。</Alert>}
    {permissions?.view && <>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {['from', 'to'].map((key) => <Grid item xs={12} sm={6} md={2} key={key}>
            <TextField fullWidth size="small" type="date" label={key === 'from' ? '作業起日' : '作業迄日'} value={filters[key]}
              InputLabelProps={{ shrink: true }} onChange={(event) => setFilters({ ...filters, [key]: event.target.value })} />
          </Grid>)}
          <Grid item xs={12} sm={8} md={4}><TextField fullWidth size="small" label="圖號／單號／批號／客戶／人員" value={filters.keyword}
            inputProps={{ maxLength: 100 }} onChange={(event) => setFilters({ ...filters, keyword: event.target.value })} /></Grid>
          <Grid item xs={12} sm={4} md={2}><TextField select fullWidth size="small" label="狀態" value={filters.status}
            onChange={(event) => setFilters({ ...filters, status: event.target.value })}>
            <MenuItem value="Active">有效</MenuItem><MenuItem value="Voided">已作廢</MenuItem><MenuItem value="">全部</MenuItem>
          </TextField></Grid>
          <Grid item xs={12} md={2}><Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={search} disabled={loading || busy}>查詢</Button>
            {permissions.create && <Button variant="contained" onClick={create} disabled={busy}>新增</Button>}
          </Stack></Grid>
        </Grid>
      </Paper>
      <TableContainer component={Paper} variant="outlined"><Table size="small">
        <TableHead><TableRow>{['作業日期', '需求單號', '生產批號', '圖號', '品名', '需求量', '良品', '不良', '單位', '生產人員', '狀態', '操作'].map((x) => <TableCell key={x}>{x}</TableCell>)}</TableRow></TableHead>
        <TableBody>
          {result.items.map((row) => <TableRow key={row.id} hover>
            <TableCell>{row.workDate.slice(0, 10)}</TableCell><TableCell>{row.sourceDocumentNo}</TableCell><TableCell>{row.productionBatchNo}</TableCell><TableCell>{row.drawingNo}</TableCell><TableCell>{row.itemName}</TableCell><TableCell>{row.demandQuantity ?? '—'}</TableCell>
            <TableCell>{row.goodQuantity}</TableCell><TableCell>{row.defectQuantity}</TableCell><TableCell>{row.unit}</TableCell>
            <TableCell>{row.operatorName}</TableCell><TableCell>{row.status === 'Active' ? '有效' : '已作廢'}</TableCell>
            <TableCell><Button onClick={() => open(row)} disabled={busy || loading}>查看</Button></TableCell>
          </TableRow>)}
          {!result.items.length && <TableRow><TableCell colSpan={12} align="center">{loading ? '查詢中…' : query ? '查無資料' : '請設定日期後按查詢'}</TableCell></TableRow>}
        </TableBody>
      </Table></TableContainer>
      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
        <Typography variant="body2">共 {result.total} 筆／每頁 100 筆／第 {page} 頁</Typography>
        <Button disabled={page <= 1 || loading || busy} onClick={() => setPage((n) => n - 1)}>上一頁</Button>
        <Button disabled={!query || page * 100 >= result.total || loading || busy} onClick={() => setPage((n) => n + 1)}>下一頁</Button>
      </Stack>
    </>}
    <Dialog open={Boolean(dialog)} onClose={close} fullWidth maxWidth="lg">
      <DialogTitle>{dialog === 'create' ? '新增成品報工' : dialog === 'edit' ? '修改成品報工' : '成品報工明細'}{form.reportNo && <Typography variant="body2">{form.reportNo}</Typography>}</DialogTitle>
      <DialogContent dividers>
        {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
        {form.status === 'Voided' && <Alert severity="warning" sx={{ mb: 2 }}>已作廢：{form.voidReason}</Alert>}
        <Typography variant="h6" sx={{ mb: 2 }}>需求單資料</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {field('需求單號', 'sourceDocumentNo', 100)}{field('來源項次', 'sourceLineNo', 30)}{field('客戶名稱', 'customerName', 200)}
          {field('機種', 'model', 100)}{field('需求數量', 'demandQuantity', undefined, false, 'number')}
          {field('預定交貨日', 'plannedDeliveryDate', undefined, false, 'date')}{field('原始交貨日文字', 'deliveryDateText', 300)}
          {field('生產批號', 'productionBatchNo', 100)}{field('需求備註', 'demandRemark', 1000)}
        </Grid>
        {form.sourceKind !== 'Manual' && <Box sx={{ mb: 2 }}>
          <Typography variant="body2">來源：{form.sourceKind === 'Excel' ? 'Excel' : '圖片辨識'}／{form.sourceFileName}／{form.sourceSheet}／列 {form.sourceRow || '—'}</Typography>
          <FormControlLabel control={<Checkbox checked={Boolean(form.sourceConfirmed)} disabled={!editable || busy} onChange={(event) => change('sourceConfirmed', event.target.checked)} />}
            label="已核對需求單內容，並確認屬於目前登入公司" />
        </Box>}
        <Typography variant="h6" sx={{ mb: 2 }}>本次實際報工（需求數量不代表完工量）</Typography>
        <Grid container spacing={2}>
          {field('作業日期', 'workDate', undefined, true, 'date')}
          {field('成品圖號', 'drawingNo', 100, true)}{field('品名', 'itemName', 200)}
          {field('規格', 'specification', 300)}{field('單位', 'unit', 20, true)}
          {field('良品數量', 'goodQuantity', undefined, true, 'number')}
          {field('生產人員', 'operatorName', 100, true)}{field('工作站', 'workstation', 100)}{field('班別', 'shift', 50)}
          <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="備註" value={form.remark} disabled={!editable || busy}
            inputProps={{ maxLength: 1000 }} onChange={(event) => change('remark', event.target.value)} /></Grid>
        </Grid>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 3, mb: 1 }}>
          <Typography variant="h6">不良明細</Typography><Chip label={`合計 ${defectTotal}`} />
          {editable && <Button disabled={busy || form.defects.length >= 50} onClick={() => change('defects', [...form.defects, { reason: '', quantity: '' }])}>新增不良原因</Button>}
        </Stack>
        {!form.defects.length && <Typography color="text.secondary">無不良紀錄</Typography>}
        {form.defects.map((defect, index) => <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} key={index} sx={{ mb: 1 }}>
          <TextField fullWidth size="small" label="不良原因" value={defect.reason} disabled={!editable || busy} inputProps={{ maxLength: 200 }}
            onChange={(event) => change('defects', form.defects.map((x, i) => i === index ? { ...x, reason: event.target.value } : x))} />
          <TextField size="small" label="不良數量" type="number" value={defect.quantity} disabled={!editable || busy} inputProps={{ min: 0.0001, step: '0.0001' }}
            onChange={(event) => change('defects', form.defects.map((x, i) => i === index ? { ...x, quantity: event.target.value } : x))} />
          {editable && <Button color="error" disabled={busy} onClick={() => change('defects', form.defects.filter((_, i) => i !== index))}>移除</Button>}
        </Stack>)}
        {form.createdUtc && <Box sx={{ mt: 2 }}>
          <Typography variant="body2">填報：{form.createdByName}／{timestamp(form.createdUtc)}</Typography>
          <Typography variant="body2">最後異動：{form.updatedByName}／{timestamp(form.updatedUtc)}</Typography>
        </Box>}
        {history.length > 0 && <Table size="small" sx={{ mt: 2 }}><TableHead><TableRow><TableCell>異動時間</TableCell><TableCell>人員</TableCell><TableCell>動作／內容</TableCell></TableRow></TableHead>
          <TableBody>{history.map((entry) => <TableRow key={entry.id}><TableCell>{timestamp(entry.occurredUtc)}</TableCell><TableCell>{entry.actorName}</TableCell>
            <TableCell>{actionNames[entry.action] || entry.action}<details><summary>查看前後資料</summary>
              <Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>修改前：{entry.beforeJson || '無'}{'\n'}修改後：{entry.afterJson}</Typography>
            </details></TableCell></TableRow>)}</TableBody></Table>}
      </DialogContent>
      <DialogActions>
        {dialog === 'view' && <Button disabled={busy} onClick={loadHistory}>異動歷程</Button>}
        {dialog === 'view' && form.status === 'Active' && permissions?.update && <Button disabled={busy} onClick={() => { setDialog('edit'); setHistory([]); }}>修改</Button>}
        {dialog === 'view' && form.status === 'Active' && permissions?.delete && <Button color="error" disabled={busy} onClick={() => { setVoidReason(''); setVoidOpen(true); }}>刪除（作廢）</Button>}
        <Button onClick={close} disabled={busy}>關閉</Button>
        {editable && <Button variant="contained" onClick={save} disabled={busy}>{busy ? '儲存中…' : '儲存'}</Button>}
      </DialogActions>
    </Dialog>
    <Dialog open={voidOpen} onClose={() => { if (!busy) setVoidOpen(false); }} fullWidth maxWidth="sm">
      <DialogTitle>作廢報工紀錄</DialogTitle><DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>作廢後不可修改，原始資料及異動歷程會保留。</Alert>
        {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
        <TextField fullWidth autoFocus multiline label="作廢原因" value={voidReason} inputProps={{ maxLength: 500 }} disabled={busy} onChange={(event) => setVoidReason(event.target.value)} />
      </DialogContent><DialogActions><Button disabled={busy} onClick={() => setVoidOpen(false)}>取消</Button>
        <Button color="error" variant="contained" disabled={busy || !voidReason.trim()} onClick={remove}>確認作廢</Button></DialogActions>
    </Dialog>
    <DemandImportDialog open={importOpen} onClose={() => setImportOpen(false)} companies={permissions?.companies || []}
      onApply={(fields) => { setForm({ ...newForm(), ...fields }); setHistory([]); setFormError(''); setDirty(true); setDialog('create'); setImportOpen(false); }} />
  </Box>;
}
